import { t } from 'i18next'
import _ from 'lodash'
import { enqueueSnackbar } from 'notistack'
import { FieldErrors, FieldValues } from 'react-hook-form'

import { reset } from 'features/user'
import { store } from 'providers/redux'
import { browserRelativeRedirect } from 'providers/router/utils'
import { ROUTES } from 'routes'
import { GraphQlResponse } from './response'
import { TypedDocumentNode } from './types'
import { gql } from './utils'

export const ERROR_FRAGMENT: TypedDocumentNode<MutationInputError> = gql`
  fragment ErrorFragment on Error {
    attribute
    message
  }
`

export const ERROR_CODE = {
  EXPIRED_TOKEN: 'EXPIRED TOKEN',
  FORBIDDEN: 'FORBIDDEN',
  INTERNAL_SERVER_ERROR: 'INTERNAL SERVER ERROR',
  INVALID_TOKEN: 'INVALID TOKEN',
  NOT_FOUND: 'NOT FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
} as const

export type MutationInputError = {
  attribute: string
  message: string
}

export type ServerError = Pick<Error, 'message'> & {
  extensions?: {
    code?: string
  }
}

const enqueueSnackbarForErrorCode = (code: string): void => {
  const text = _.startCase(code.toLowerCase())
  enqueueSnackbar(text, { variant: 'error' })
}

const enqueueSnackbarForError200 = (): void => {
  const message = t('error.communicationError', { ns: 'providers/tanstackQuery' })
  enqueueSnackbar(message, { variant: 'error' })
}

export const enqueueSnackbarForError500 = (message: ServerError['message']): void => {
  const title = t('error.serverError', { ns: 'providers/tanstackQuery' })
  enqueueSnackbar(`${title}: ${message}`, { variant: 'error' })
}

export const handleErrors200 = (errors: NonNullable<GraphQlResponse['errors']>) => {
  errors.forEach((error: ServerError) => {
    const code: string | undefined = error.extensions?.code
    if (code === undefined) {
      enqueueSnackbarForError200()
      return
    }

    switch (code) {
      case ERROR_CODE.EXPIRED_TOKEN:
      case ERROR_CODE.INVALID_TOKEN: {
        enqueueSnackbarForErrorCode(code)
        break
      }
      case ERROR_CODE.FORBIDDEN:
      case ERROR_CODE.NOT_FOUND: {
        enqueueSnackbarForErrorCode(code)
        browserRelativeRedirect(ROUTES.BASE.NOT_FOUND)
        break
      }
      case ERROR_CODE.INTERNAL_SERVER_ERROR: {
        enqueueSnackbarForErrorCode(code)
        store.dispatch(reset())
        browserRelativeRedirect(ROUTES.AUTH.LOGIN)
        break
      }
      case ERROR_CODE.UNAUTHORIZED: {
        break
      }
      default: {
        enqueueSnackbarForError200()
      }
    }
  })
}

/**
 * Map GraphQL mutation errors to `react-hook-form` errors
 */
export const mapGraphQLErrors = <T extends FieldValues>(
  graphQLErrors: MutationInputError[],
  attributeReplacer?: (attribute: MutationInputError['attribute']) => string,
): FieldErrors<T> => {
  const result: FieldErrors<T> = {}

  Object.assign(
    result,
    ...graphQLErrors.map(
      (error: MutationInputError): Record<string, { message: string }> | void => {
        const key: MutationInputError['attribute'] = attributeReplacer
          ? attributeReplacer(error.attribute)
          : error.attribute

        const { message } = error
        if (key.includes('.')) {
          // convert path string (e.g. 'foo[2].bar[3]') into object structure
          _.set(result, key, { message })
        } else {
          return {
            [key]: { message },
          }
        }
      },
    ),
  )

  return result
}
